<template>
  <div class="subscriptions-page">
    <PageHeader
      title="Subscriptions"
      subtitle="Compare plans, change tier, or request a custom setup."
    />

    <v-alert
      v-if="current_subscription && current_subscription.state === 'past_due'"
      type="warning"
      icon="mdi-credit-card-clock-outline"
      prominent
      :value="true"
      class="subscriptions-page__payment-alert mb-6"
    >
      <div class="subscriptions-page__payment-alert-body">
        <div class="subscriptions-page__payment-alert-text">
          <strong>There&rsquo;s a problem with your subscription payment.</strong>
          Your {{ current_subscription.plan.name }} plan has an overdue payment,
          so API access is paused. Update your payment method to retry, or cancel
          the current subscription to switch to the Free plan.
        </div>
        <div class="subscriptions-page__payment-alert-actions">
          <v-btn
            v-if="current_subscription.update_url"
            color="primary"
            depressed
            @click="open_update_payment(current_subscription.update_url)"
          >Update payment method</v-btn>
          <v-btn
            v-if="current_subscription.cancel_url"
            outlined
            color="error"
            @click="show_past_due_cancel_dialog = true"
          >Cancel subscription</v-btn>
        </div>
      </div>
    </v-alert>

    <v-tabs
      v-model="current_tab"
      class="subscriptions-page__tabs mb-6"
      color="primary"
      grow
    >
      <v-tab>Web Scraping API</v-tab>
      <v-tab>Residential Proxies</v-tab>
    </v-tabs>

    <v-tabs-items v-model="current_tab" class="transparent">
      <!-- Web Scraping API -->
      <v-tab-item>
        <v-row>
          <v-col
            v-for="plan in plans"
            :key="plan.id"
            cols="12"
            sm="6"
            md="4"
            lg="3"
          >
            <article
              class="pricing-card"
              :class="{ 'pricing-card--current': plan.can_cancel }"
            >
              <header class="pricing-card__head">
                <div class="pricing-card__name-row">
                  <h3 class="pricing-card__name">{{ plan.name }}</h3>
                  <v-chip
                    v-if="plan.can_cancel"
                    color="success"
                    small
                    label
                    class="pricing-card__chip"
                  >Current</v-chip>
                </div>
                <p class="pricing-card__subtitle">
                  {{ plan.credits | currency("", 0) }} API credits
                </p>
              </header>

              <div class="pricing-card__price">
                <template v-if="plan.price !== 0">
                  <span class="pricing-card__currency">$</span>
                  <span class="pricing-card__amount">{{
                    (plan.price / 100) | currency("", 0)
                  }}</span>
                </template>
                <span v-else class="pricing-card__free">Free</span>
                <span class="pricing-card__period">/ month</span>
              </div>

              <ul class="pricing-card__features">
                <li
                  v-for="(feature, index) in plan.features"
                  :key="feature.name + index"
                  :class="{ 'is-disabled': !feature.included }"
                >
                  <v-icon
                    size="18"
                    :color="feature.included ? 'success' : 'grey lighten-1'"
                  >{{
                    feature.included
                      ? "mdi-check-circle"
                      : "mdi-minus-circle-outline"
                  }}</v-icon>
                  <span>{{ feature.name }}</span>
                </li>
              </ul>

              <div class="pricing-card__cta">
                <v-btn
                  v-if="!plan.can_cancel"
                  color="primary"
                  block
                  depressed
                  :disabled="!plan.can_subscribe"
                  @click="start_checkout(plan.product_id)"
                >{{ get_subscribe_button_text(plan) }}</v-btn>
                <v-btn
                  v-else
                  outlined
                  color="error"
                  block
                  :disabled="plan.price === 0"
                  @click="show_cancel_dialogue"
                >Cancel subscription</v-btn>
              </div>
            </article>
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- Residential -->
      <v-tab-item>
        <v-row>
          <v-col
            v-for="plan in residential_plans"
            :key="plan.id"
            cols="12"
            sm="6"
            md="4"
            lg="3"
          >
            <article
              class="pricing-card"
              :class="{ 'pricing-card--current': plan.can_cancel }"
            >
              <header class="pricing-card__head">
                <div class="pricing-card__name-row">
                  <h3 class="pricing-card__name">{{ plan.name }}</h3>
                  <v-chip
                    v-if="plan.can_cancel"
                    color="success"
                    small
                    label
                    class="pricing-card__chip"
                  >Current</v-chip>
                </div>
                <p class="pricing-card__subtitle">
                  {{ plan.traffic_amount_gb | currency("", 0) }} GB included
                </p>
              </header>

              <div class="pricing-card__price">
                <template v-if="plan.price !== 0">
                  <span class="pricing-card__currency">$</span>
                  <span class="pricing-card__amount">{{
                    (plan.price / 100) | currency("", 0)
                  }}</span>
                </template>
                <span v-else class="pricing-card__free">Free</span>
                <span class="pricing-card__period">/ month</span>
              </div>
              <p class="pricing-card__per-unit">
                ${{ (plan.price / plan.traffic_amount_gb / 100).toFixed(2) }}
                / GB
              </p>

              <ul class="pricing-card__features">
                <li
                  v-for="(feature, index) in plan.features"
                  :key="feature.name + index"
                  :class="{ 'is-disabled': !feature.included }"
                >
                  <v-icon
                    size="18"
                    :color="feature.included ? 'success' : 'grey lighten-1'"
                  >{{
                    feature.included
                      ? "mdi-check-circle"
                      : "mdi-minus-circle-outline"
                  }}</v-icon>
                  <span>{{ feature.name }}</span>
                </li>
              </ul>

              <div class="pricing-card__cta">
                <v-btn
                  v-if="!plan.can_cancel"
                  color="primary"
                  block
                  depressed
                  :disabled="!plan.can_subscribe"
                  @click="start_residential_checkout(plan.paddle_plan_id)"
                >{{ get_residential_subscribe_button_text(plan) }}</v-btn>
                <v-btn
                  v-else
                  outlined
                  color="error"
                  block
                  :disabled="plan.price === 0"
                  @click="show_residential_cancel_dialogue"
                >Cancel subscription</v-btn>
              </div>
            </article>
          </v-col>
        </v-row>
      </v-tab-item>
    </v-tabs-items>

    <SectionCard
      title="Need more? Order a custom plan"
      subtitle="Tell us about your workload and we'll tailor pricing, integrations, and support to your team."
      class="subscriptions-page__custom mt-8"
    >
      <ul class="subscriptions-page__custom-features">
        <li v-for="item in customFeatures" :key="item">
          <v-icon size="18" color="primary">mdi-check-circle</v-icon>
          <span>{{ item }}</span>
        </li>
      </ul>
      <template #actions>
        <v-btn color="primary" depressed @click="requestCustomPlan"
          >Request a custom plan</v-btn
        >
      </template>
    </SectionCard>

    <ConfirmDialog
      :value="show_subscription_created_dialog"
      title="Subscription created 🎉"
      message="The changes will be applied within 15 minutes (usually almost instantly)."
      confirm-label="Got it"
      cancel-label=""
      @confirm="onSubscriptionChangedCreatedDialog"
      @input="show_subscription_created_dialog = $event"
    />

    <ConfirmDialog
      :value="show_subscription_cancelled_dialog"
      title="Subscription cancelled"
      message="The changes will be applied within 15 minutes (usually almost instantly)."
      confirm-label="Got it"
      cancel-label=""
      @confirm="onSubscriptionChangedCancelDialog"
      @input="show_subscription_cancelled_dialog = $event"
    />

    <ConfirmDialog
      :value="show_residential_cancel_first_dialog"
      title="Cancel your current subscription first"
      confirm-label="Cancel current subscription"
      cancel-label="Keep current plan"
      confirm-color="error"
      :max-width="560"
      @confirm="onCurrentResidentialSubscriptionCancel"
      @input="show_residential_cancel_first_dialog = $event"
    >
      <p>
        To start a new subscription, please <strong>cancel</strong> your current
        one first.
      </p>
      <p>
        This action stops renewal — you'll keep using the plan until the end of
        the billing cycle.
      </p>
      <p>Once you subscribe again, the current plan's GBs will be lost.</p>
    </ConfirmDialog>

    <ConfirmDialog
      :value="show_past_due_cancel_dialog"
      title="Cancel your current subscription first"
      confirm-label="Cancel current subscription"
      cancel-label="Keep current plan"
      confirm-color="error"
      :max-width="560"
      @confirm="onPastDueSubscriptionCancel"
      @input="show_past_due_cancel_dialog = $event"
    >
      <p>
        Your current subscription has a <strong>payment problem</strong>.
        To start a new subscription, please <strong>cancel</strong> it first.
      </p>
      <p>
        Remaining API credits will <strong>not</strong> be transferred to the
        new subscription.
      </p>
    </ConfirmDialog>

    <ConfirmDialog
      :value="show_cancel_first_dialog"
      title="Cancel your current subscription first"
      confirm-label="Cancel current subscription"
      cancel-label="Keep current plan"
      confirm-color="error"
      :max-width="560"
      @confirm="onCurrentSubscriptionCancel"
      @input="show_cancel_first_dialog = $event"
    >
      <p>
        To start a new subscription, please <strong>cancel</strong> your current
        one first.
      </p>
      <p>
        Remaining API credits will <strong>not</strong> be transferred to the
        new subscription.
      </p>
    </ConfirmDialog>

    <ConfirmDialog
      :value="show_cancel_dialog"
      title="Cancel current subscription?"
      confirm-label="Cancel subscription"
      cancel-label="Keep current plan"
      confirm-color="error"
      :max-width="560"
      @confirm="onCurrentSubscriptionCancel"
      @input="show_cancel_dialog = $event"
    >
      <p v-if="current_subscription">
        This action takes effect immediately. You won't be able to use
        <strong>{{ current_subscription.remained_credits }}</strong>
        remaining credits.
      </p>
      <p>
        Remaining API credits will <strong>not</strong> be transferred to the
        new subscription.
      </p>
    </ConfirmDialog>

    <ConfirmDialog
      :value="show_residential_cancel_dialog"
      title="Cancel current subscription?"
      confirm-label="Cancel subscription"
      cancel-label="Keep current plan"
      confirm-color="error"
      :max-width="520"
      @confirm="onCurrentResidentialSubscriptionCancel"
      @input="show_residential_cancel_dialog = $event"
    >
      <p>Your plan won't be renewed after the current billing cycle.</p>
    </ConfirmDialog>

    <div class="loading-overlay" v-if="loading">
      <v-progress-circular
        :size="50"
        :width="2"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </div>
  </div>
</template>

<script>
import { delay } from "@/utils/common_utils";
import constants from "@/config/constants";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import ConfirmDialog from "@/components/shared/ConfirmDialog";

export default {
  name: "Subscriptions",
  components: { PageHeader, SectionCard, ConfirmDialog },
  data() {
    return {
      tabs: ["webscrapingapi", "residential"],
      current_tab: 0,
      loading: false,
      plans: [],
      residential_plans: [],
      current_subscription: null,
      current_residential_subscription: null,
      show_past_due_cancel_dialog: false,
      show_cancel_first_dialog: false,
      show_residential_cancel_first_dialog: false,
      show_subscription_created_dialog: false,
      show_subscription_cancelled_dialog: false,
      show_cancel_dialog: false,
      show_residential_cancel_dialog: false,
      user_email: "",
      customFeatures: [
        "Live calls integration",
        "Custom scraping code",
        "Personal recommendations",
        "Bulk pricing",
        "Custom proxy pools",
      ],
    };
  },
  watch: {
    current_tab(newTab) {
      this.$router.push({ query: { tab: this.tabs[newTab] } });
    },
  },
  methods: {
    get_subscribe_button_text(plan) {
      if (plan.price === 0) {
        if (
          this.current_subscription &&
          plan.name === this.current_subscription.plan.name
        ) {
          return "Subscribed";
        } else {
          return "Subscribe";
        }
      } else {
        return "Subscribe";
      }
    },
    get_residential_subscribe_button_text(plan) {
      if (plan.price === 0) {
        if (
          this.current_residential_subscription &&
          plan.name === this.current_residential_subscription.plan.name
        ) {
          return "Subscribed";
        } else {
          return "Subscribe";
        }
      } else {
        return "Subscribe";
      }
    },
    get_available_plans() {
      this.loading = true;
      Promise.all([
        this.$store.dispatch("subscriptions/getPlans"),
        this.$store.dispatch("subscriptions/getResidentialPlans"),
      ]).then(
        (res) => {
          this.loading = false;
          this.plans = res[0].data;
          this.residential_plans = res[1].data;
        },
        (err) => {
          this.loading = false;
          console.log(err);
        }
      );
    },
    get_current_subscription() {
      this.$store
        .dispatch("subscriptions/getCurrentSubscription")
        .then((res) => {
          this.current_subscription = res.data.subscriptions[0];
        }, console.log);
    },
    get_current_residential_subscription() {
      this.$store
        .dispatch("subscriptions/getCurrentResidentialSubscription")
        .then((res) => {
          this.current_residential_subscription = res;
        }, console.log);
    },
    get_current_user() {
      this.$store.dispatch("auth/get_user").then((res) => {
        this.user_email = res.data.email;
      }, console.log);
    },
    start_checkout(product_id) {
      if (this.current_subscription && this.current_subscription.state === 'past_due') {
        this.show_past_due_cancel_dialog = true;
      } else if (this.current_subscription.plan.price > 0) {
        this.show_cancel_first_dialog = true;
      } else {
        // eslint-disable-next-line no-undef
        Paddle.Checkout.open({
          product: product_id,
          email: this.user_email,
          disableLogout: true,
          successCallback: (data) => {
            // eslint-disable-next-line no-undef
            trackConversion(data);
            this.show_subscription_created_dialog = true;
            this.$gtag.event(constants.ANALYTICS_EVENTS.CHECK_OUT_SUCCESS, {
              event_category: constants.ANALYTICS_EVENTS.PAYMENT_CATEGORY,
              value: product_id,
            });
          },
          closeCallback: () => {
            this.get_current_subscription();
            this.get_current_residential_subscription();
            this.get_available_plans();
            this.$gtag.event(constants.ANALYTICS_EVENTS.CHECK_OUT_CANCELLED, {
              event_category: constants.ANALYTICS_EVENTS.PAYMENT_CATEGORY,
              value: product_id,
            });
          },
        });
      }
    },
    start_residential_checkout(product_id) {
      if (this.current_residential_subscription.recurring) {
        this.show_residential_cancel_first_dialog = true;
      } else {
        // eslint-disable-next-line no-undef
        Paddle.Checkout.open({
          product: product_id,
          email: this.user_email,
          disableLogout: true,
          successCallback: (data) => {
            // eslint-disable-next-line no-undef
            trackConversion(data);
            this.show_subscription_created_dialog = true;
            this.$gtag.event(constants.ANALYTICS_EVENTS.CHECK_OUT_SUCCESS, {
              event_category: constants.ANALYTICS_EVENTS.PAYMENT_CATEGORY,
              value: product_id,
            });
          },
          closeCallback: () => {
            this.get_current_subscription();
            this.get_current_residential_subscription();
            this.get_available_plans();
            this.$gtag.event(constants.ANALYTICS_EVENTS.CHECK_OUT_CANCELLED, {
              event_category: constants.ANALYTICS_EVENTS.PAYMENT_CATEGORY,
              value: product_id,
            });
          },
        });
      }
    },
    show_cancel_dialogue() {
      this.show_cancel_dialog = true;
    },
    show_residential_cancel_dialogue() {
      this.show_residential_cancel_dialog = true;
    },
    start_cancel(cancel_url, product_id) {
      // eslint-disable-next-line no-undef
      Paddle.Checkout.open({
        override: cancel_url,
        successCallback: () => {
          this.show_subscription_cancelled_dialog = true;
          this.$gtag.event(constants.ANALYTICS_EVENTS.CANCEL_SUCCESS, {
            event_category: constants.ANALYTICS_EVENTS.PAYMENT_CATEGORY,
            value: product_id,
          });
        },
        closeCallback: () => {
          this.get_current_subscription();
          this.get_current_residential_subscription();
          this.get_available_plans();
          this.$gtag.event(constants.ANALYTICS_EVENTS.CANCEL_CANCELLED, {
            event_category: constants.ANALYTICS_EVENTS.PAYMENT_CATEGORY,
          });
        },
      });
    },
    get_available_plans_delayed() {
      this.loading = true;
      delay(4000).then(() => {
        this.get_current_subscription();
        this.get_current_residential_subscription();
        this.get_available_plans();
      });
    },
    onCurrentSubscriptionCancel() {
      this.show_cancel_first_dialog = false;
      this.show_cancel_dialog = false;
      this.start_cancel(this.current_subscription.cancel_url);
    },
    onCurrentResidentialSubscriptionCancel() {
      this.show_residential_cancel_first_dialog = false;
      this.show_residential_cancel_dialog = false;
      this.start_cancel(this.current_residential_subscription.cancel_url);
    },
    onSubscriptionChangedCancelDialog() {
      this.show_subscription_cancelled_dialog = false;
      this.get_available_plans_delayed();
    },
    onSubscriptionChangedCreatedDialog() {
      this.show_subscription_created_dialog = false;
      this.get_available_plans_delayed();
    },
    open_update_payment(update_url) {
      // eslint-disable-next-line no-undef
      Paddle.Checkout.open({
        override: update_url,
        closeCallback: () => {
          this.get_current_subscription();
          this.get_available_plans();
        },
      });
    },
    onPastDueSubscriptionCancel() {
      this.show_past_due_cancel_dialog = false;
      if (this.current_subscription && this.current_subscription.cancel_url) {
        this.start_cancel(this.current_subscription.cancel_url);
      }
    },
    requestCustomPlan() {
      window.open(
        "mailto:support@scrapingant.com?subject=Custom Plan Order | ScrapingAnt"
      );
    },
  },
  mounted() {
    this.get_current_subscription();
    this.get_current_residential_subscription();
    this.get_available_plans();
    this.get_current_user();

    const tab = this.$route.query.tab;
    if (this.tabs.indexOf(tab) !== -1) {
      this.current_tab = this.tabs.indexOf(tab);
    }
  },
};
</script>

<style scoped lang="scss">
.subscriptions-page {
  position: relative;

  &__tabs {
    ::v-deep .v-tabs-bar {
      background: transparent !important;
      border-bottom: 1px solid rgba(15, 23, 42, 0.08);
    }

    ::v-deep .v-tab {
      text-transform: none;
      letter-spacing: 0;
      font-weight: 500;
      font-size: 14px;
      color: rgba(15, 23, 42, 0.6) !important;

      &--active {
        color: #0f172a !important;
        font-weight: 600;
      }
    }
  }

  &__payment-alert {
    ::v-deep .v-alert {
      border-radius: 12px;
      align-items: flex-start;
    }

    ::v-deep .v-alert__wrapper {
      align-items: flex-start;
    }
  }

  &__payment-alert-body {
    display: flex;
    flex-direction: column;
    gap: 14px;
    width: 100%;
  }

  &__payment-alert-text {
    font-size: 14px;
    line-height: 1.55;
    color: rgba(15, 23, 42, 0.82);
  }

  &__payment-alert-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;

    .v-btn {
      text-transform: none;
      letter-spacing: 0;
      font-weight: 600;
      font-size: 13px;
    }
  }

  &__custom {
    background: linear-gradient(
      135deg,
      rgba(226, 120, 47, 0.04),
      rgba(15, 23, 42, 0.02)
    );
  }

  &__custom-features {
    list-style: none;
    padding: 0;
    margin: 0 0 8px;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 10px 24px;

    li {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 13px;
      color: rgba(15, 23, 42, 0.78);
    }
  }
}

.pricing-card {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  padding: 24px 22px;
  transition: border-color 160ms ease, box-shadow 160ms ease,
    transform 160ms ease;

  &:hover {
    border-color: rgba(226, 120, 47, 0.4);
    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.08);
    transform: translateY(-2px);
  }

  &--current {
    border-color: #2ecc71;
    box-shadow: 0 4px 16px rgba(46, 204, 113, 0.12);
  }

  &__head {
    margin-bottom: 16px;
  }

  &__name-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 4px;
  }

  &__name {
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
  }

  &__chip {
    text-transform: none;
    font-weight: 600;
    font-size: 11px !important;
    height: 22px !important;
  }

  &__subtitle {
    font-size: 12px;
    color: rgba(15, 23, 42, 0.55);
    margin: 0;
  }

  &__price {
    display: flex;
    align-items: baseline;
    gap: 2px;
    margin-bottom: 4px;
  }

  &__currency {
    font-size: 18px;
    font-weight: 600;
    color: rgba(15, 23, 42, 0.7);
    margin-right: 2px;
  }

  &__amount {
    font-size: 36px;
    font-weight: 800;
    color: #0f172a;
    line-height: 1;
    letter-spacing: -0.02em;
  }

  &__free {
    font-size: 32px;
    font-weight: 800;
    color: #0f172a;
    line-height: 1;
  }

  &__period {
    font-size: 13px;
    color: rgba(15, 23, 42, 0.55);
    margin-left: 6px;
  }

  &__per-unit {
    font-size: 12px;
    color: rgba(15, 23, 42, 0.55);
    margin: 0 0 16px;
  }

  &__features {
    list-style: none;
    padding: 0;
    margin: 16px 0 24px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;

    li {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      font-size: 13px;
      line-height: 1.45;
      color: rgba(15, 23, 42, 0.82);

      .v-icon {
        margin-top: 1px;
        flex-shrink: 0;
      }

      &.is-disabled {
        color: rgba(15, 23, 42, 0.4);
        text-decoration: line-through;
      }
    }
  }

  &__cta {
    margin-top: auto;

    .v-btn {
      text-transform: none;
      letter-spacing: 0;
      font-weight: 600;
      font-size: 13px;
    }
  }
}
</style>
